import {
   AfterContentInit,
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   ContentChild,
   ElementRef,
   EventEmitter,
   Input,
   OnChanges,
   OnDestroy,
   Output,
   TemplateRef,
   ViewEncapsulation,
} from '@angular/core';
import { NgChanges } from 'src/lib/ng-changes';

@Component({
   encapsulation: ViewEncapsulation.None,
   changeDetection: ChangeDetectionStrategy.OnPush,
   selector: 'ndt-paged-table-wrapper',
   templateUrl: './paged-table-wrapper.component.html',
   styleUrls: ['./paged-table-wrapper.component.scss'],
   host: {
      class: 'paged-table-wrapper-wrapper',
   },
   standalone: false,
})
export class PagedTableWrapperComponent implements OnChanges, AfterContentInit, OnDestroy {
   @ContentChild('pagedTable') pagedTable: TemplateRef<any>;

   @Input() dataSource: any[];

   @Input() rowHeight: number = 25;

   /* 
      Input allows rowHeightOffsetting to take place
      This is primarly done in cases where 2 rows per item in the 
      DataSource are rendered thus adjusting how many rows can be visible
      based on the height of the second row needs to be accounted for
   */
   @Input() calculateWithRowHeightOffset: boolean = false;

   @Input() totalPagesOverride: number | null = null;

   @Input() hidePagination: boolean = false;

   @Output() pageChange = new EventEmitter<{ pageNumber: number; pageSize: number }>();

   totalPages: number = 0;

   tableRows: number = 4;

   selectedPage: number = 1;

   private resizeObserver: ResizeObserver | null = null;

   private observedHeight = 0;

   get beforeDisabled(): boolean {
      return this.selectedPage === 1;
   }

   get nextDisabled(): boolean {
      return this.selectedPage === this.totalPages;
   }

   get filteredDataSource(): any[] {
      if (this.totalPagesOverride != null) return [...this.dataSource];

      const selectedPageIndex = this.selectedPage - 1;
      if (this.dataSource) {
         return this.getFilteredDataSource(selectedPageIndex, this.tableRows) ?? [];
      }
      return [];
   }

   get tableElement(): HTMLTableElement | null {
      return this.ref.nativeElement?.querySelector('table.lcs-table') ?? null;
   }

   get tableBodyElement(): HTMLTableSectionElement | null {
      return this.tableElement?.querySelector('tbody') ?? null;
   }

   get tableBodyElementHeight(): number {
      const tableHeaderHeight = this.tableElement?.querySelector('thead')?.getBoundingClientRect().height ?? 0;
      const paginationBarHeight =
         this.ref.nativeElement?.querySelector('div.paged-table-pagination')?.getBoundingClientRect().height ?? 0;
      const estimatedTableBodyHeight =
         this.ref.nativeElement.getBoundingClientRect().height - tableHeaderHeight - paginationBarHeight;
      return estimatedTableBodyHeight > 0 ? estimatedTableBodyHeight : 0;
   }

   get tableBodyRowElements(): NodeListOf<HTMLTableRowElement> | null {
      return this.tableBodyElement?.querySelectorAll('tr.lcs-row') ?? null;
   }

   constructor(private ref: ElementRef, private cd: ChangeDetectorRef) {}

   ngAfterContentInit() {
      requestAnimationFrame(() => {
         this.initializeTableRows();
         this.observeResize();
      });
   }

   ngOnDestroy(): void {
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
   }

   ngOnChanges(changes: NgChanges<PagedTableWrapperComponent>): void {
      if (changes.rowHeight && !changes.rowHeight.firstChange) {
         requestAnimationFrame(() => {
            this.recalculateTableRows();
         });
      }
      if (changes.dataSource) {
         requestAnimationFrame(() => {
            this.updateTotalPages();
            this.setTableStyles();
            //this.handlePageChange(this.totalPagesOverride == null);
            // If we need to offset rows, wait until the current frame has fully rendered so the table
            // height and the pagination heights are set
            requestAnimationFrame(() => {
               this.handleRowHeightOffsets();
            });
         });
      }
   }

   incrementPages(): void {
      this.selectedPage = this.selectedPage >= this.totalPages ? this.totalPages : this.selectedPage + 1;
      this.handlePageChange();
   }

   incrementToMaxPages(): void {
      this.selectedPage = this.totalPages;
      this.handlePageChange();
   }

   isSelectedPage(page: number): boolean {
      return page === this.selectedPage;
   }

   onPageItemClick(page: number): void {
      this.selectedPage = page;
      this.handlePageChange();
   }

   reducePages(): void {
      this.selectedPage = this.selectedPage <= 1 ? 1 : this.selectedPage - 1;
      this.handlePageChange();
   }

   reduceToMinPages(): void {
      this.selectedPage = 1;
      this.handlePageChange();
   }

   handlePageChange(emitEvent = true): void {
      if (emitEvent) {
         this.pageChange.emit({ pageNumber: this.selectedPage, pageSize: this.tableRows });
      }
      this.selectedPage === this.totalPages ? this.unsetTableStyles() : this.setTableStyles();
      this.cd.detectChanges();
   }

   private getFilteredDataSource(selectedPageIndex: number, tableRows: number) {
      this.updateTotalPages();
      return [...(this.dataSource ?? [])].slice(selectedPageIndex * tableRows, (selectedPageIndex + 1) * tableRows);
   }

   private unsetTableStyles() {
      requestAnimationFrame(() => {
         if (this.tableElement) {
            //this.tableElement.style.flexGrow = 'unset';
            const filterDataLength = this.getFilteredDataSource(this.selectedPage - 1, this.tableRows).length;
            this.tableElement.style.height = filterDataLength * this.rowHeight + 'px';
            this.tableBodyRowElements?.forEach((r) => {
               r.style.height = this.rowHeight + 'px';
            });
         }
      });
   }

   private setTableStyles() {
      requestAnimationFrame(() => {
         if (this.tableElement) {
            this.tableElement.style.flexGrow = '1';
            this.tableElement.style.height = 'unset';
            this.tableBodyRowElements?.forEach((r) => {
               r.style.height = this.rowHeight + 'px';
            });
         }
      });
   }

   private observeResize(): void {
      if (typeof ResizeObserver === 'undefined' || this.resizeObserver) return;

      this.observedHeight = this.ref.nativeElement.getBoundingClientRect().height;
      this.resizeObserver = new ResizeObserver(() => {
         const height = this.ref.nativeElement.getBoundingClientRect().height;
         if (Math.abs(height - this.observedHeight) < 1) return;
         this.observedHeight = height;
         this.recalculateTableRows();
      });
      this.resizeObserver.observe(this.ref.nativeElement);
   }

   private recalculateTableRows(): void {
      const tableRows = Math.floor(this.tableBodyElementHeight / this.rowHeight);
      const next = tableRows > 0 ? tableRows : 1;
      if (next === this.tableRows) return;

      this.tableRows = next;
      this.updateTotalPages();
      this.selectedPage = Math.min(Math.max(this.selectedPage, 1), this.totalPages);
      this.handlePageChange();
   }

   private initializeTableRows(): void {
      const tableRows = Math.floor(this.tableBodyElementHeight / this.rowHeight);
      this.tableRows = tableRows > 0 ? tableRows : 1;

      requestAnimationFrame(() => {
         this.handlePageChange();
         // If we need to offset rows, wait until the current frame has fully rendered so the table
         // height and the pagination heights are set
         requestAnimationFrame(() => {
            this.handleRowHeightOffsets();
         });
      });
   }

   private handleRowHeightOffsets() {
      if (this.calculateWithRowHeightOffset) {
         // Get the max height the table can fill
         const hostHeight = this.ref.nativeElement?.getBoundingClientRect().height ?? 0;
         // Get the current height of the table
         const tableHeight = this.tableElement?.getBoundingClientRect().height ?? 0;
         // Get the current height of the pagination footer
         const paginationHeight =
            this.ref.nativeElement?.querySelector('div.paged-table-pagination')?.getBoundingClientRect().height ?? 0;
         // Get the max height the table can fill without the pagination footer
         const maxTableHeight = hostHeight - paginationHeight;

         // If the table is more than overlapping with the footer,
         // adjust the rowHeightOffset to reduce the number of rows shown
         const diff = tableHeight - maxTableHeight;
         if (diff > 0) {
            const rowsOverWithOffset = Math.round(diff / this.rowHeight);
            const tableRows = this.tableRows - rowsOverWithOffset;
            this.tableRows = tableRows > 0 ? tableRows : 1;
            requestAnimationFrame(() => {
               this.handlePageChange();
            });
         }
      }
   }

   private updateTotalPages() {
      this.totalPages = Math.max(
         this.totalPagesOverride ?? Math.ceil((this.dataSource ?? []).length / this.tableRows),
         1
      );
   }
}
